import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { User } from 'oidc-client-ts';
import { config } from '../config';
import { liveUser } from './sessionRenewal';
import { POST_LOGOUT_REDIRECT_URI, dropSession, getUserManager } from './userManager';

export type AuthStatus = 'loading' | 'signed-out' | 'signed-in';

export interface AuthContextValue {
  status: AuthStatus;
  user: User | null;
  /** Starts the redirect to the IdP. `loginHint` pre-fills the email; `redirect` is where to land afterwards. */
  signIn(opts?: { loginHint?: string; redirect?: string }): Promise<void>;
  /** Ends the IdP session when the issuer supports it, otherwise drops the local session. */
  signOut(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

type AuthState = { status: AuthStatus; user: User | null };

/**
 * Follows the stored user and the UserManager events; the effect unsubscribes everything it registered. An expired id
 * token (a tab that slept past the renewal) is renewed with the refresh token before the session is dropped.
 */
function useAuthState(): AuthState {
  const [state, setState] = useState<AuthState>({ status: 'loading', user: null });

  useEffect(() => {
    const um = getUserManager();
    let alive = true;
    void liveUser(um).then((u) => {
      if (!alive) return;
      setState(u ? { status: 'signed-in', user: u } : { status: 'signed-out', user: null });
    });
    const onLoaded = (u: User) => setState({ status: 'signed-in', user: u });
    const onUnloaded = () => setState({ status: 'signed-out', user: null });
    const onExpired = () => void liveUser(um).then((u) => {
      if (!u) void dropSession('expired');
    });
    um.events.addUserLoaded(onLoaded);
    um.events.addUserUnloaded(onUnloaded);
    um.events.addAccessTokenExpired(onExpired);
    return () => {
      alive = false;
      um.events.removeUserLoaded(onLoaded);
      um.events.removeUserUnloaded(onUnloaded);
      um.events.removeAccessTokenExpired(onExpired);
    };
  }, []);
  return state;
}

async function startSignIn(opts?: { loginHint?: string; redirect?: string }): Promise<void> {
  await getUserManager().signinRedirect({
    state: { redirect: opts?.redirect ?? '/' },
    ...(opts?.loginHint ? { login_hint: opts.loginHint } : {}),
  });
}

async function endSession(): Promise<void> {
  const um = getUserManager();
  try {
    // Cognito's /logout wants client_id + logout_uri; standard issuers ignore the extras.
    await um.signoutRedirect({ extraQueryParams: { client_id: config.clientId ?? '', logout_uri: POST_LOGOUT_REDIRECT_URI } });
  } catch {
    await dropSession('signed-out');
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const state = useAuthState();
  const value = useMemo<AuthContextValue>(() => ({ ...state, signIn: startSignIn, signOut: endSession }), [state]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const v = useContext(AuthContext);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
